(() => {
  // storefront-src/storefront-accessibility.mjs
  var CONTROL_SELECTORS = Object.freeze({
    panel: "[data-ax-xp]",
    opener: "[data-ax-xp-open]",
    contrast: 'input[name="ax-contrast"]',
    rain: "[data-sl-rain-pause]"
  });
  var STORAGE_KEYS = Object.freeze({
    motion: "axtlan-experience",
    preferences: "axtlan-experience-prefs",
    rotation: "axtlan-rotation-fx",
    rain: "speclang.shop.rain.v1"
  });
  var modes = ["complete", "light", "off"];
  var sourceKeys = ["experience", "rainControls", "rainMain"];
  var sha = (value) => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
  function createStorefrontAccessibility({ doc, win, binding = null, expectedSources = null } = {}) {
    let active = true;
    const unavailable = Object.freeze({
      available: false,
      deviceLocal: true,
      snapshot: () => null,
      chooseMotion: () => ({ applied: false, persisted: false }),
      setRainOff: () => ({ applied: false, persisted: false }),
      setHighContrast: () => ({ applied: false, persisted: false }),
      destroy() {
        active = false;
      }
    });
    if (!doc || !win || doc.defaultView !== win || doc.documentElement !== win.document?.documentElement || binding?.kind !== "reviewed-storefront-source" || binding.status !== "current" || binding.origin !== win.location?.origin || !sourceKeys.every((key) => sha(expectedSources?.[key]) && binding.sources?.[key] === expectedSources[key])) return unavailable;
    const root = doc.documentElement;
    const panel = doc.querySelector(CONTROL_SELECTORS.panel);
    const contrast = panel?.querySelector(CONTROL_SELECTORS.contrast);
    const rain = doc.querySelector(CONTROL_SELECTORS.rain);
    const inputs = Object.fromEntries(modes.map((mode) => [mode, panel?.querySelector('input[name="ax-motion"][value="' + mode + '"]')]));
    if (!panel || !contrast || !rain || !modes.every((mode) => inputs[mode])) return unavailable;
    const reduced = win.matchMedia("(prefers-reduced-motion: reduce)");
    const forced = win.matchMedia("(forced-colors: active)");
    const read = (key) => {
      try {
        return win.localStorage.getItem(key);
      } catch {
        return null;
      }
    };
    const json = (key) => {
      try {
        const value = JSON.parse(read(key));
        return value && typeof value === "object" && !Array.isArray(value) ? value : {};
      } catch {
        return {};
      }
    };
    const change = (input) => input.dispatchEvent(new win.Event("change", { bubbles: true }));
    const result = (applied, persisted) => ({ applied, persisted: Boolean(applied && persisted) });
    function chooseMotion(mode) {
      if (!active || !modes.includes(mode) || mode !== "off" && (reduced.matches || forced.matches)) return result(false, false);
      inputs[mode].checked = true;
      change(inputs[mode]);
      const applied = root.getAttribute("data-ax-mode") === mode;
      const rotation = mode === "complete" ? "advanced" : mode === "light" ? "lightweight" : "off";
      return result(applied, read(STORAGE_KEYS.motion) === mode && read(STORAGE_KEYS.rotation) === rotation);
    }
    function setRainOff(off) {
      if (!active || typeof off !== "boolean" || !off && (reduced.matches || forced.matches)) return result(false, false);
      if (root.hasAttribute("data-sl-rain-paused") !== off) rain.click();
      return result(root.hasAttribute("data-sl-rain-paused") === off, json(STORAGE_KEYS.rain).paused === off);
    }
    function setHighContrast(high) {
      if (!active || typeof high !== "boolean") return result(false, false);
      contrast.checked = high;
      change(contrast);
      return result(root.getAttribute("data-ax-contrast") === "high" === high, json(STORAGE_KEYS.preferences).contrast === high);
    }
    const protectMotion = () => {
      if (active && (reduced.matches || forced.matches)) chooseMotion("off");
    };
    reduced.addEventListener("change", protectMotion);
    forced.addEventListener("change", protectMotion);
    protectMotion();
    return Object.freeze({
      available: true,
      deviceLocal: true,
      chooseMotion,
      setRainOff,
      setHighContrast,
      snapshot() {
        if (!active) return null;
        const motion = root.getAttribute("data-ax-mode");
        if (!modes.includes(motion)) return null;
        return {
          motion,
          reducedMotion: motion === "off" || reduced.matches,
          rainOff: motion !== "complete" || root.hasAttribute("data-sl-rain-paused") || reduced.matches || forced.matches || root.getAttribute("data-ax-contrast") === "high",
          explicitRainPaused: root.hasAttribute("data-sl-rain-paused"),
          rainEligibleByPreferences: motion === "complete" && !root.hasAttribute("data-sl-rain-paused") && !reduced.matches && !forced.matches && root.getAttribute("data-ax-contrast") !== "high",
          rendererRunning: null,
          highContrast: root.getAttribute("data-ax-contrast") === "high",
          deviceLocal: true
        };
      },
      destroy() {
        active = false;
        reduced.removeEventListener("change", protectMotion);
        forced.removeEventListener("change", protectMotion);
      }
    });
  }
  function accessibilityCopy(locale, labels) {
    const copy = labels?.[locale];
    return copy && ["controls", "reduce", "reduceBody", "contrast", "contrastBody", "rainPause", "rainResume"].every((key) => typeof copy[key] === "string" && copy[key].length > 0) ? copy : null;
  }

  // storefront-src/storefront-integration.mjs
  function installStorefrontAccessibility({ doc, win, binding = null, expectedSources = null } = {}) {
    const controller = createStorefrontAccessibility({ doc, win, binding, expectedSources });
    if (!controller.available) return controller;
    const destroy = () => {
      win.removeEventListener("pagehide", release);
      controller.destroy();
    };
    const release = (event) => {
      if (event?.persisted !== true) destroy();
    };
    win.addEventListener("pagehide", release);
    return Object.freeze({ ...controller, destroy });
  }

  // storefront-src/accessibility-labels.json
  var accessibility_labels_default = {
    ar: {
      controls: "\u0639\u0646\u0627\u0635\u0631 \u0627\u0644\u062A\u062D\u0643\u0645 \u0628\u0627\u0644\u062A\u062C\u0631\u0628\u0629",
      reduce: "\u062A\u0642\u0644\u064A\u0644 \u0627\u0644\u062D\u0631\u0643\u0629",
      reduceBody: "\u064A\u0648\u0642\u0641 \u0627\u0644\u0627\u0646\u062A\u0642\u0627\u0644\u0627\u062A \u0648\u0627\u0644\u062A\u062A\u0627\u0628\u0639\u0627\u062A \u0648\u0627\u0644\u062E\u0631\u0627\u0626\u0637 \u0627\u0644\u0645\u062A\u062D\u0631\u0643\u0629.",
      contrast: "\u062A\u0628\u0627\u064A\u0646 \u0645\u062D\u0633\u0651\u0646",
      contrastBody: "\u062D\u062F\u0648\u062F \u0648\u0639\u0646\u0627\u0635\u0631 \u062A\u062D\u0643\u0645 \u0623\u0648\u0636\u062D.",
      rainPause: "\u0625\u064A\u0642\u0627\u0641 \u0627\u0644\u0645\u0637\u0631 \u0645\u0624\u0642\u062A\u064B\u0627",
      rainResume: "\u0627\u0633\u062A\u0626\u0646\u0627\u0641 \u0627\u0644\u0645\u0637\u0631"
    },
    de: {
      controls: "Erlebnissteuerung",
      reduce: "Bewegung reduzieren",
      reduceBody: "Stoppt \xDCberg\xE4nge, Kaskaden und animierte Karten.",
      contrast: "Erh\xF6hter Kontrast",
      contrastBody: "Deutlichere Konturen und Bedienelemente.",
      rainPause: "Regen pausieren",
      rainResume: "Regen fortsetzen"
    },
    el: {
      controls: "\u03A7\u03B5\u03B9\u03C1\u03B9\u03C3\u03C4\u03AE\u03C1\u03B9\u03B1 \u03B5\u03BC\u03C0\u03B5\u03B9\u03C1\u03AF\u03B1\u03C2",
      reduce: "\u039C\u03B5\u03AF\u03C9\u03C3\u03B7 \u03BA\u03AF\u03BD\u03B7\u03C3\u03B7\u03C2",
      reduceBody: "\u03A3\u03C4\u03B1\u03BC\u03B1\u03C4\u03AC \u03BC\u03B5\u03C4\u03B1\u03B2\u03AC\u03C3\u03B5\u03B9\u03C2, \u03B1\u03BA\u03BF\u03BB\u03BF\u03C5\u03B8\u03AF\u03B5\u03C2 \u03BA\u03B1\u03B9 \u03BA\u03B9\u03BD\u03BF\u03CD\u03BC\u03B5\u03BD\u03BF\u03C5\u03C2 \u03C7\u03AC\u03C1\u03C4\u03B5\u03C2.",
      contrast: "\u0395\u03BD\u03B9\u03C3\u03C7\u03C5\u03BC\u03AD\u03BD\u03B7 \u03B1\u03BD\u03C4\u03AF\u03B8\u03B5\u03C3\u03B7",
      contrastBody: "\u03A0\u03B9\u03BF \u03B5\u03C5\u03B4\u03B9\u03AC\u03BA\u03C1\u03B9\u03C4\u03B1 \u03C0\u03B5\u03C1\u03B9\u03B3\u03C1\u03AC\u03BC\u03BC\u03B1\u03C4\u03B1 \u03BA\u03B1\u03B9 \u03C7\u03B5\u03B9\u03C1\u03B9\u03C3\u03C4\u03AE\u03C1\u03B9\u03B1.",
      rainPause: "\u03A0\u03B1\u03CD\u03C3\u03B7 \u03C4\u03B7\u03C2 \u03B2\u03C1\u03BF\u03C7\u03AE\u03C2",
      rainResume: "\u03A3\u03C5\u03BD\u03AD\u03C7\u03B9\u03C3\u03B7 \u03C4\u03B7\u03C2 \u03B2\u03C1\u03BF\u03C7\u03AE\u03C2"
    },
    en: {
      controls: "Experience controls",
      reduce: "Reduce motion",
      reduceBody: "Stops transitions, cascades and animated maps.",
      contrast: "Enhanced contrast",
      contrastBody: "More distinct outlines and controls.",
      rainPause: "Pause the rain",
      rainResume: "Resume the rain"
    },
    es: {
      controls: "Controles de experiencia",
      reduce: "Reducir movimiento",
      reduceBody: "Detiene transiciones, cascadas y mapas animados.",
      contrast: "Contraste reforzado",
      contrastBody: "Contornos y controles m\xE1s definidos.",
      rainPause: "Pausar la lluvia",
      rainResume: "Reanudar la lluvia"
    },
    fa: {
      controls: "\u06A9\u0646\u062A\u0631\u0644\u200C\u0647\u0627\u06CC \u062A\u062C\u0631\u0628\u0647",
      reduce: "\u06A9\u0627\u0647\u0634 \u062D\u0631\u06A9\u062A",
      reduceBody: "\u06AF\u0630\u0627\u0631\u0647\u0627\u060C \u0622\u0628\u0634\u0627\u0631\u0647\u0627 \u0648 \u0646\u0642\u0634\u0647\u200C\u0647\u0627\u06CC \u067E\u0648\u06CC\u0627\u0646\u0645\u0627\u06CC\u06CC\u200C\u0634\u062F\u0647 \u0631\u0627 \u0645\u062A\u0648\u0642\u0641 \u0645\u06CC\u200C\u06A9\u0646\u062F.",
      contrast: "\u06A9\u0646\u062A\u0631\u0627\u0633\u062A \u0628\u06CC\u0634\u062A\u0631",
      contrastBody: "\u062E\u0637\u0648\u0637 \u0648 \u06A9\u0646\u062A\u0631\u0644\u200C\u0647\u0627\u06CC \u0645\u062A\u0645\u0627\u06CC\u0632\u062A\u0631.",
      rainPause: "\u062A\u0648\u0642\u0641 \u0628\u0627\u0631\u0627\u0646",
      rainResume: "\u0627\u062F\u0627\u0645\u0647\u0654 \u0628\u0627\u0631\u0627\u0646"
    },
    fr: {
      controls: "R\xE9glages de l\u2019exp\xE9rience",
      reduce: "R\xE9duire les animations",
      reduceBody: "Arr\xEAte les transitions, les cascades et les cartes anim\xE9es.",
      contrast: "Contraste renforc\xE9",
      contrastBody: "Contours et commandes mieux diff\xE9renci\xE9s.",
      rainPause: "Mettre la pluie en pause",
      rainResume: "Reprendre la pluie"
    },
    hi: {
      controls: "\u0905\u0928\u0941\u092D\u0935 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923",
      reduce: "\u0917\u0924\u093F \u0918\u091F\u093E\u090F\u0901",
      reduceBody: "\u0938\u0902\u0915\u094D\u0930\u092E\u0923, \u0915\u094D\u0930\u092E\u093F\u0915 \u092A\u094D\u0930\u092D\u093E\u0935 \u0914\u0930 \u0938\u091C\u0940\u0935 \u092E\u093E\u0928\u091A\u093F\u0924\u094D\u0930 \u0930\u094B\u0915\u0924\u093E \u0939\u0948\u0964",
      contrast: "\u092C\u0947\u0939\u0924\u0930 \u0915\u0902\u091F\u094D\u0930\u093E\u0938\u094D\u091F",
      contrastBody: "\u0905\u0927\u093F\u0915 \u0938\u094D\u092A\u0937\u094D\u091F \u0938\u0940\u092E\u093E\u0930\u0947\u0916\u093E\u090F\u0901 \u0914\u0930 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923\u0964",
      rainPause: "\u092C\u093E\u0930\u093F\u0936 \u0930\u094B\u0915\u0947\u0902",
      rainResume: "\u092C\u093E\u0930\u093F\u0936 \u092B\u093F\u0930 \u0936\u0941\u0930\u0942 \u0915\u0930\u0947\u0902"
    },
    hu: {
      controls: "\xC9lm\xE9nybe\xE1ll\xEDt\xE1sok",
      reduce: "Mozg\xE1s cs\xF6kkent\xE9se",
      reduceBody: "Le\xE1ll\xEDtja az \xE1tmeneteket, a l\xE9pcs\u0151zetes effekteket \xE9s az anim\xE1lt t\xE9rk\xE9peket.",
      contrast: "Fokozott kontraszt",
      contrastBody: "Hat\xE1rozottabb k\xF6rvonalak \xE9s vez\xE9rl\u0151k.",
      rainPause: "Es\u0151 sz\xFCneteltet\xE9se",
      rainResume: "Es\u0151 folytat\xE1sa"
    },
    id: {
      controls: "Kontrol pengalaman",
      reduce: "Kurangi gerakan",
      reduceBody: "Menghentikan transisi, efek beruntun, dan peta animasi.",
      contrast: "Kontras ditingkatkan",
      contrastBody: "Garis tepi dan kontrol dibuat lebih tegas.",
      rainPause: "Jeda hujan",
      rainResume: "Lanjutkan hujan"
    },
    ja: {
      controls: "\u4F53\u9A13\u8A2D\u5B9A",
      reduce: "\u52D5\u304D\u3092\u6E1B\u3089\u3059",
      reduceBody: "\u753B\u9762\u9077\u79FB\u3001\u9023\u7D9A\u6F14\u51FA\u3001\u5730\u56F3\u306E\u30A2\u30CB\u30E1\u30FC\u30B7\u30E7\u30F3\u3092\u505C\u6B62\u3057\u307E\u3059\u3002",
      contrast: "\u30B3\u30F3\u30C8\u30E9\u30B9\u30C8\u306E\u5F37\u5316",
      contrastBody: "\u3088\u308A\u660E\u78BA\u306A\u30A2\u30A6\u30C8\u30E9\u30A4\u30F3\u3068\u30B3\u30F3\u30C8\u30ED\u30FC\u30EB\u3002",
      rainPause: "\u96E8\u3092\u4E00\u6642\u505C\u6B62",
      rainResume: "\u96E8\u3092\u518D\u958B"
    },
    ko: {
      controls: "\uCCB4\uD5D8 \uC124\uC815",
      reduce: "\uC6C0\uC9C1\uC784 \uC904\uC774\uAE30",
      reduceBody: "\uD654\uBA74 \uC804\uD658, \uC5F0\uC18D \uD6A8\uACFC, \uC9C0\uB3C4 \uC560\uB2C8\uBA54\uC774\uC158\uC744 \uBA48\uCDA5\uB2C8\uB2E4.",
      contrast: "\uD5A5\uC0C1\uB41C \uB300\uBE44",
      contrastBody: "\uB354\uC6B1 \uB69C\uB837\uD55C \uC724\uACFD\uC120\uACFC \uCEE8\uD2B8\uB864.",
      rainPause: "\uBE44 \uC77C\uC2DC \uC815\uC9C0",
      rainResume: "\uBE44 \uB2E4\uC2DC \uC7AC\uC0DD"
    },
    "pt-BR": {
      controls: "Controles de experi\xEAncia",
      reduce: "Reduzir movimento",
      reduceBody: "Interrompe transi\xE7\xF5es, cascatas e mapas animados.",
      contrast: "Contraste refor\xE7ado",
      contrastBody: "Contornos e controles mais distintos.",
      rainPause: "Pausar a chuva",
      rainResume: "Retomar a chuva"
    },
    ru: {
      controls: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440\u0430",
      reduce: "\u0423\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u044C \u0430\u043D\u0438\u043C\u0430\u0446\u0438\u044E",
      reduceBody: "\u041E\u0442\u043A\u043B\u044E\u0447\u0430\u0435\u0442 \u043F\u0435\u0440\u0435\u0445\u043E\u0434\u044B, \u043A\u0430\u0441\u043A\u0430\u0434\u043D\u044B\u0435 \u044D\u0444\u0444\u0435\u043A\u0442\u044B \u0438 \u0430\u043D\u0438\u043C\u0430\u0446\u0438\u044E \u043A\u0430\u0440\u0442.",
      contrast: "\u041F\u043E\u0432\u044B\u0448\u0435\u043D\u043D\u044B\u0439 \u043A\u043E\u043D\u0442\u0440\u0430\u0441\u0442",
      contrastBody: "\u0411\u043E\u043B\u0435\u0435 \u0440\u0430\u0437\u043B\u0438\u0447\u0438\u043C\u044B\u0435 \u043A\u043E\u043D\u0442\u0443\u0440\u044B \u0438 \u044D\u043B\u0435\u043C\u0435\u043D\u0442\u044B \u0443\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u044F.",
      rainPause: "\u041F\u0440\u0438\u043E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C \u0434\u043E\u0436\u0434\u044C",
      rainResume: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u0434\u043E\u0436\u0434\u044C"
    },
    ta: {
      controls: "\u0B85\u0BA9\u0BC1\u0BAA\u0BB5\u0B95\u0BCD \u0B95\u0B9F\u0BCD\u0B9F\u0BC1\u0BAA\u0BCD\u0BAA\u0BBE\u0B9F\u0BC1\u0B95\u0BB3\u0BCD",
      reduce: "\u0B87\u0BAF\u0B95\u0BCD\u0B95\u0BA4\u0BCD\u0BA4\u0BC8\u0B95\u0BCD \u0B95\u0BC1\u0BB1\u0BC8\u0B95\u0BCD\u0B95",
      reduceBody: "\u0BAE\u0BBE\u0BB1\u0BCD\u0BB1\u0B99\u0BCD\u0B95\u0BB3\u0BCD, \u0BA4\u0BCA\u0B9F\u0BB0\u0BCD \u0BB5\u0BBF\u0BB3\u0BC8\u0BB5\u0BC1\u0B95\u0BB3\u0BCD, \u0B85\u0B9A\u0BC8\u0BAF\u0BC1\u0BAE\u0BCD \u0BB5\u0BB0\u0BC8\u0BAA\u0B9F\u0B99\u0BCD\u0B95\u0BB3\u0BC8 \u0BA8\u0BBF\u0BB1\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1\u0B95\u0BBF\u0BB1\u0BA4\u0BC1.",
      contrast: "\u0BAE\u0BC7\u0BAE\u0BCD\u0BAA\u0B9F\u0BCD\u0B9F \u0BB5\u0BC7\u0BB1\u0BC1\u0BAA\u0BBE\u0B9F\u0BC1",
      contrastBody: "\u0BAE\u0BC7\u0BB2\u0BC1\u0BAE\u0BCD \u0BA4\u0BC6\u0BB3\u0BBF\u0BB5\u0BBE\u0BA9 \u0BB5\u0BB0\u0BC8\u0BAF\u0BB1\u0BC8\u0B95\u0BB3\u0BC1\u0BAE\u0BCD \u0B95\u0B9F\u0BCD\u0B9F\u0BC1\u0BAA\u0BCD\u0BAA\u0BBE\u0B9F\u0BC1\u0B95\u0BB3\u0BC1\u0BAE\u0BCD.",
      rainPause: "\u0BAE\u0BB4\u0BC8\u0BAF\u0BC8 \u0B87\u0B9F\u0BC8\u0BA8\u0BBF\u0BB1\u0BC1\u0BA4\u0BCD\u0BA4\u0BC1",
      rainResume: "\u0BAE\u0BB4\u0BC8\u0BAF\u0BC8\u0BA4\u0BCD \u0BA4\u0BCA\u0B9F\u0BB0\u0BCD"
    },
    th: {
      controls: "\u0E15\u0E31\u0E27\u0E04\u0E27\u0E1A\u0E04\u0E38\u0E21\u0E1B\u0E23\u0E30\u0E2A\u0E1A\u0E01\u0E32\u0E23\u0E13\u0E4C",
      reduce: "\u0E25\u0E14\u0E01\u0E32\u0E23\u0E40\u0E04\u0E25\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E2B\u0E27",
      reduceBody: "\u0E2B\u0E22\u0E38\u0E14\u0E01\u0E32\u0E23\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E1C\u0E48\u0E32\u0E19 \u0E40\u0E2D\u0E1F\u0E40\u0E1F\u0E01\u0E15\u0E4C\u0E15\u0E48\u0E2D\u0E40\u0E19\u0E37\u0E48\u0E2D\u0E07 \u0E41\u0E25\u0E30\u0E41\u0E1C\u0E19\u0E17\u0E35\u0E48\u0E40\u0E04\u0E25\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E2B\u0E27",
      contrast: "\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E23\u0E35\u0E22\u0E1A\u0E15\u0E48\u0E32\u0E07",
      contrastBody: "\u0E17\u0E33\u0E43\u0E2B\u0E49\u0E40\u0E2A\u0E49\u0E19\u0E02\u0E2D\u0E1A\u0E41\u0E25\u0E30\u0E15\u0E31\u0E27\u0E04\u0E27\u0E1A\u0E04\u0E38\u0E21\u0E21\u0E2D\u0E07\u0E40\u0E2B\u0E47\u0E19\u0E44\u0E14\u0E49\u0E0A\u0E31\u0E14\u0E02\u0E36\u0E49\u0E19",
      rainPause: "\u0E2B\u0E22\u0E38\u0E14\u0E1D\u0E19\u0E0A\u0E31\u0E48\u0E27\u0E04\u0E23\u0E32\u0E27",
      rainResume: "\u0E40\u0E25\u0E48\u0E19\u0E1D\u0E19\u0E15\u0E48\u0E2D"
    },
    tr: {
      controls: "Deneyim denetimleri",
      reduce: "Hareketi azalt",
      reduceBody: "Ge\xE7i\u015Fleri, ard\u0131\u015F\u0131k efektleri ve hareketli haritalar\u0131 durdurur.",
      contrast: "Art\u0131r\u0131lm\u0131\u015F kar\u015F\u0131tl\u0131k",
      contrastBody: "Daha belirgin kenarlar ve denetimler.",
      rainPause: "Ya\u011Fmuru duraklat",
      rainResume: "Ya\u011Fmuru s\xFCrd\xFCr"
    },
    ur: {
      controls: "\u062A\u062C\u0631\u0628\u06D2 \u06A9\u06D2 \u06A9\u0646\u0679\u0631\u0648\u0644",
      reduce: "\u062D\u0631\u06A9\u062A \u06A9\u0645 \u06A9\u0631\u06CC\u06BA",
      reduceBody: "\u0645\u0646\u062A\u0642\u0644\u06CC\u0627\u06BA\u060C \u0633\u0644\u0633\u0644\u06D2 \u0627\u0648\u0631 \u0645\u062A\u062D\u0631\u06A9 \u0646\u0642\u0634\u06D2 \u0631\u0648\u06A9 \u062F\u06CC\u062A\u0627 \u06C1\u06D2\u06D4",
      contrast: "\u0628\u0691\u06BE\u0627 \u06C1\u0648\u0627 \u062A\u0636\u0627\u062F",
      contrastBody: "\u0632\u06CC\u0627\u062F\u06C1 \u0646\u0645\u0627\u06CC\u0627\u06BA \u062D\u0627\u0634\u06CC\u06D2 \u0627\u0648\u0631 \u06A9\u0646\u0679\u0631\u0648\u0644\u06D4",
      rainPause: "\u0628\u0627\u0631\u0634 \u0631\u0648\u06A9\u06CC\u06BA",
      rainResume: "\u0628\u0627\u0631\u0634 \u062F\u0648\u0628\u0627\u0631\u06C1 \u0634\u0631\u0648\u0639 \u06A9\u0631\u06CC\u06BA"
    },
    vi: {
      controls: "C\xE1c \u0111i\u1EC1u khi\u1EC3n tr\u1EA3i nghi\u1EC7m",
      reduce: "Gi\u1EA3m chuy\u1EC3n \u0111\u1ED9ng",
      reduceBody: "D\u1EEBng c\xE1c chuy\u1EC3n ti\u1EBFp, hi\u1EC7u \u1EE9ng n\u1ED1i ti\u1EBFp v\xE0 b\u1EA3n \u0111\u1ED3 \u0111\u1ED9ng.",
      contrast: "T\u0103ng \u0111\u1ED9 t\u01B0\u01A1ng ph\u1EA3n",
      contrastBody: "\u0110\u01B0\u1EDDng vi\u1EC1n v\xE0 \u0111i\u1EC1u khi\u1EC3n r\xF5 r\xE0ng h\u01A1n.",
      rainPause: "T\u1EA1m d\u1EEBng m\u01B0a",
      rainResume: "Ti\u1EBFp t\u1EE5c m\u01B0a"
    },
    "zh-CN": {
      controls: "\u4F53\u9A8C\u63A7\u5236",
      reduce: "\u51CF\u5C11\u8FD0\u52A8",
      reduceBody: "\u505C\u6B62\u8FC7\u6E21\u3001\u7EA7\u8054\u548C\u52A8\u753B\u5730\u56FE\u3002",
      contrast: "\u589E\u5F3A\u5BF9\u6BD4\u5EA6",
      contrastBody: "\u66F4\u6E05\u6670\u7684\u8F6E\u5ED3\u548C\u63A7\u5236\u3002",
      rainPause: "\u6682\u505C\u4E0B\u96E8",
      rainResume: "\u7EE7\u7EED\u4E0B\u96E8"
    }
  };

  // storefront-src/storefront-entry.mjs
  function bootStorefrontAccessibility(doc, win, configuration = null) {
    const locale = doc.documentElement.lang;
    if (!accessibilityCopy(locale, accessibility_labels_default)) return { available: false, destroy() {
    } };
    return installStorefrontAccessibility({
      doc,
      win,
      binding: configuration?.binding ?? null,
      expectedSources: configuration?.expectedSources ?? null
    });
  }
  function start() {
    const node = document.getElementById("speclang-accessibility-binding-r143");
    let configuration = null;
    try {
      configuration = JSON.parse(node?.textContent ?? "null");
    } catch {
    }
    bootStorefrontAccessibility(document, window, configuration);
  }
  if (typeof document !== "undefined") {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
    else start();
  }
})();
